"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTokenManager = void 0;
const react_1 = require("react");
const handleTokenExpiration_1 = require("./handleTokenExpiration");
const handleLogout_1 = require("./handleLogout");
const CookieStateStore_1 = require("./CookieStateStore");
const CHECK_INTERVAL_MS = 60000;
const BROADCAST_CHANNEL = 'moment-auth';
/**
 * Drop-in replacement for the setInterval auth useEffect in each App.tsx.
 *
 * Handles:
 *  - Periodic token expiration checks (keyed on expires_at, not the auth object)
 *  - visibilitychange — immediate check when the tab/app regains focus
 *  - online — immediate check when network connectivity is restored
 *  - BroadcastChannel — listens for logout signals from sibling apps
 *  - Silent sign-in on startup when a cross-subdomain session cookie exists
 *    but local localStorage is empty
 */
const useTokenManager = (auth, options = {}) => {
    const startupSilentSignIn = options.startupSilentSignIn ?? 'cookie-only';
    const expiresAt = auth.user?.expires_at ?? null;
    const expiresAtRef = (0, react_1.useRef)(expiresAt);
    expiresAtRef.current = expiresAt;
    // Stable auth reference to avoid recreating effects on every auth object change
    const authRef = (0, react_1.useRef)(auth);
    authRef.current = auth;
    const hasAttemptedStartupSilentAuth = (0, react_1.useRef)(false);
    // Attempt silent sign-in after initial auth load completes (not on first mount
    // while isLoading is still true — that race caused missed session restoration).
    (0, react_1.useEffect)(() => {
        if (auth.isLoading || auth.isAuthenticated || hasAttemptedStartupSilentAuth.current) {
            return;
        }
        const cookie = (0, CookieStateStore_1.getSessionCookie)();
        const hasValidSessionCookie = cookie !== null && cookie.expiresAt * 1000 > Date.now();
        const shouldAttempt = startupSilentSignIn === 'always' || hasValidSessionCookie;
        if (!shouldAttempt) {
            return;
        }
        hasAttemptedStartupSilentAuth.current = true;
        auth.signinSilent().catch(() => {
            // No active Cognito session — user can sign in normally
        });
    }, [auth.isLoading, auth.isAuthenticated, startupSilentSignIn, auth]);
    // Periodic check + visibility + online + BroadcastChannel listeners
    (0, react_1.useEffect)(() => {
        const check = () => (0, handleTokenExpiration_1.handleTokenExpiration)(authRef.current);
        check();
        const interval = setInterval(check, CHECK_INTERVAL_MS);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible')
                check();
        };
        const onOnline = () => check();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('online', onOnline);
        let bc = null;
        try {
            bc = new BroadcastChannel(BROADCAST_CHANNEL);
            bc.onmessage = (event) => {
                if (event.data?.type === 'logout') {
                    (0, handleLogout_1.handleLogout)(authRef.current);
                }
            };
        }
        catch {
            // BroadcastChannel not available in this environment
        }
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('online', onOnline);
            bc?.close();
        };
        // Keyed on expiresAt so the interval restarts only when the token itself changes,
        // not on every auth object reference change
    }, [expiresAt]); // eslint-disable-line react-hooks/exhaustive-deps
};
exports.useTokenManager = useTokenManager;
